/**
 * One screening call in the attempts timeline: attempt number, status chip,
 * times, duration, disconnect reason, recording link (new tab) and the
 * transcript behind a native <details> so it costs nothing until opened.
 */
import { ExternalLink } from 'lucide-react';
import { fullDateTime, relativeTime } from '@/lib/time';
import { durationLabel, screeningStatusMeta } from '@/features/screening/screening-format';
import type { ScreeningCall } from '@/features/screening/types';

function When({ label, at }: { label: string; at: string | null }) {
  if (!at) return null;
  return (
    <div className="flex gap-2">
      <dt className="w-20 shrink-0 text-muted">{label}</dt>
      <dd className="text-gray-900">
        <time dateTime={at} title={fullDateTime(at)}>{relativeTime(at)}</time>
      </dd>
    </div>
  );
}

export function ScreeningAttempt({ call, isLatest }: { call: ScreeningCall; isLatest: boolean }) {
  const meta = screeningStatusMeta(call.status);
  const duration = durationLabel(call.duration_s);
  return (
    <li className="relative pl-6" data-testid={`screening-attempt-${call.attempt_no}`} aria-label={`Attempt ${call.attempt_no}: ${meta.label}`}>
      <span
        aria-hidden
        className={`absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 bg-white text-[12px] ${isLatest ? 'border-navy' : 'border-gray-300'}`}
      />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <span className="font-semibold text-gray-900">Attempt {call.attempt_no}</span>
        <span className={`rounded-full px-2 py-0.5 text-[12px] font-medium ${meta.chip}`} data-testid="screening-attempt-status">
          {meta.label}
        </span>
        {duration && <span className="text-muted tabular-nums">{duration}</span>}
      </div>
      {isLatest && <p className="mt-0.5 text-[12px] text-muted">{meta.hint}</p>}
      <dl className="mt-1.5 space-y-0.5 text-[12px]">
        <When label="Scheduled" at={call.scheduled_for} />
        <When label="Started" at={call.started_at} />
        <When label="Ended" at={call.ended_at} />
        {call.disconnect_reason && (
          <div className="flex gap-2">
            <dt className="w-20 shrink-0 text-muted">Ended by</dt>
            <dd className="text-gray-900">{call.disconnect_reason.replace(/_/g, ' ')}</dd>
          </div>
        )}
      </dl>
      {(call.recording_url || call.transcript) && (
        <div className="mt-2 flex flex-wrap items-start gap-x-4 gap-y-1 text-[13px]">
          {call.recording_url && (
            <a
              href={call.recording_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-10 items-center gap-1 font-medium text-navy underline-offset-2 hover:underline"
            >
              Listen to the recording
              <ExternalLink size={12} strokeWidth={2} aria-hidden />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          )}
          {call.transcript && (
            <details className="min-w-0 flex-1 basis-full">
              <summary className="inline-flex min-h-10 cursor-pointer items-center font-medium text-navy">Transcript</summary>
              <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-surface p-3 font-sans text-[13px] leading-5 text-gray-800">
                {call.transcript}
              </pre>
            </details>
          )}
        </div>
      )}
    </li>
  );
}
